import {
  Button,
  Chip,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { SLAClockContext } from '../../components/SLAChip'
import SectionTitle from '../../components/SectionTitle'
import TicketSearchInput from '../../components/TicketSearchInput'
import { TICKETS_QUERY } from '../../graphql/tickets'
import { listLoadErrorMessage } from '../../helpers/listLoadError'
import { validateTicketsResponse } from '../../helpers/ticketResponseValidation'
import {
  needsAction,
  slaUrgency,
  sortForTriage,
  assignmentKind,
  Ticket,
  TicketList,
  TicketTab,
  TICKET_TAB_LABEL
} from '../../helpers/ticketWorkflow'
import { useCursorPage } from '../../helpers/useCursorPage'
import { useDebouncedValue } from '../../helpers/useDebouncedValue'
import { getTranslate, lang } from '../../localization'
import BulkAssignBar from './BulkAssignBar'
import SupportTicketRowMobile from './SupportTicketRowMobile'
import SupportTicketsTable from './SupportTicketsTable'
import useStyles from './styles/index.styles'

/** Which quick filter is applied to the loaded page. */
type QuickFilter = 'none' | 'breaching' | 'unassigned' | 'open'

const QUICK_FILTERS: { key: QuickFilter; label: string }[] = [
  { key: 'breaching', label: 'Breaching' },
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'open', label: 'Open' }
]

interface SupportTicketsProps {
  /** Which list to show. Owned by SupportTabs so it lives in the URL. */
  tab: TicketTab
}

/**
 * One ticket list. Composition only, per §8.4 — the table, the row and the
 * skeleton are their own components.
 */
export default function SupportTickets({ tab }: SupportTicketsProps) {
  const classes = useStyles()
  const history = useHistory()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [query, setQuery] = useState('')
  // Which quick filter is active, or none. A single value rather than a set:
  // "breaching AND unassigned" is a smaller need than the confusion of two
  // chips whose combination nobody can predict from looking at them.
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('none')
  // Ids, not tickets: a ticket object in state goes stale the moment the list
  // refetches, and `version` must be read from the CURRENT row or the
  // optimistic-concurrency token is wrong. The selected tickets are derived
  // from the live list below.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  // The bar captures its items when the batch is submitted, so a row ticked
  // mid-flight is not in it. Leaving the boxes live means the operator reads
  // "3 of 5 assigned" against seven visible ticks.
  const [assigning, setAssigning] = useState(false)
  // The input shows `query`; the request uses the settled value.
  const search = useDebouncedValue(query)

  const {
    items: tickets,
    loading,
    error,
    validationError,
    hasMore,
    loadMore,
    refetch
  } = useCursorPage<{ tickets: TicketList }, Ticket>({
    query: TICKETS_QUERY,
    // `search` is part of the variables, so changing it is a new query rather
    // than a filter over what is already loaded — and a new Apollo cache entry,
    // since `search` is a key arg.
    variables: { tab, search },
    maxRetained: 500,
    getPage: (data) =>
      data && {
        items: data.tickets.tickets,
        nextPageToken: data.tickets.nextPageToken
      },
    withItems: (data, items) => ({
      tickets: { ...data.tickets, tickets: items }
    }),
    validate: validateTicketsResponse
  })

  // Two different emptinesses, and conflating them hid one of them completely.
  //
  // LOAD-emptiness needs !hasMore: an empty first page with a live cursor is
  // incomplete, not empty. That reasoning is right and is kept.
  //
  // FILTER-emptiness does not. A client-side filter empties the list the moment
  // it is applied, whatever the cursor says — and since hasMore is true until an
  // operator has paged to the very end, gating on it meant that on any list
  // longer than one page the filter-empty message NEVER RENDERED. Blank table,
  // no explanation, which is the normal case rather than an edge one.
  // `!hasMore` matters: a first page that came back empty while a next-page
  // token exists is INCOMPLETE, not empty. Without it the list would show
  // "no data" over a query that is still walking the cursor.
  const loadSettled = !loading && !error && !validationError && !hasMore
  const clientFiltered = quickFilter !== 'none'
  // Show the empty state when the load is finished, OR when a client-side
  // filter is what emptied it — the second does not wait on the cursor.
  const settled = loadSettled || (clientFiltered && !loading)

  // One clock for the whole render — calling new Date() inside the comparator
  // would let the boundary move mid-sort, which is how a comparator stops being
  // a total order.
  //
  // Held in state and ticked, not read inline: urgency is time-derived, so with
  // a render-only clock a ticket crosses due-soon into breached and keeps
  // showing the stale chip in the stale rank until something ELSE re-renders
  // the screen. Nothing here polls. An agent watching the queue would be
  // looking at an ordering that quietly stopped being true.
  //
  // 60s matches the granularity the urgency boundary is expressed in
  // (dueSoonMinutes), so a faster tick would re-render without ever changing an
  // answer.
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(id)
  }, [])

  // Filter, then order. Both act on the LOADED PAGE only — `tickets()` takes
  // neither a filter nor a sort argument, so the server still decides which
  // tickets arrive and in what order. A more urgent ticket can sit on a page
  // that has not been fetched. Stated in the count below rather than hidden.
  const visible = useMemo(() => {
    const filtered = tickets.filter((ticket: Ticket) => {
      switch (quickFilter) {
        case 'breaching':
          return slaUrgency(ticket, now) === 'breached'
        case 'unassigned':
          return assignmentKind(ticket) === 'unassigned'
        case 'open':
          return needsAction(ticket.state)
        default:
          return true
      }
    })
    return sortForTriage(filtered, now)
  }, [now, quickFilter, tickets])

  // Selection is intersected with what is CURRENTLY visible on every render.
  // A ticket that a filter change, a search or a refetch removed from the list
  // must not stay silently selected and get acted on — the operator can no
  // longer see what they would be assigning.
  const selectedTickets = useMemo(
    () => visible.filter((ticket: Ticket) => selectedIds.has(ticket.id)),
    [selectedIds, visible]
  )
  const toggleSelected = useCallback(
    (ticket: Ticket) =>
      setSelectedIds((current) => {
        const next = new Set(current)
        if (next.has(ticket.id)) next.delete(ticket.id)
        else next.add(ticket.id)
        return next
      }),
    []
  )
  const toggleAll = useCallback(
    () =>
      setSelectedIds((current) => {
        const allVisibleSelected =
          visible.length > 0 &&
          visible.every((ticket: Ticket) => current.has(ticket.id))
        return allVisibleSelected
          ? new Set()
          : new Set(visible.map((ticket: Ticket) => ticket.id))
      }),
    [visible]
  )
  const clearSelection = useCallback(() => setSelectedIds(new Set()), [])
  const openTicket = useCallback(
    (ticket: Ticket) =>
      history.push(`/${lang}/support/tickets/detail/${ticket.id}`),
    [history]
  )

  // A search that returns nothing is not an empty list, and saying so is the
  // difference between "there is nothing" and "your query matched nothing".
  // Three different empties, three different messages. "No tickets in this
  // view" is FALSE when a quick filter is on — tickets loaded, none matched —
  // and sending an agent to look for missing data when the answer is "clear the
  // filter" is the failure this distinction exists to prevent.
  const filtering = Boolean(search.trim())
  //
  // The filter case splits again on hasMore. "No loaded tickets match this
  // filter." reads as final, and it is not: `hasMore` comes from the SERVER's
  // cursor and is unaffected by a client-side filter, so Load more can fetch a
  // page containing no matches, leave the table unchanged, and stay available
  // to click again. Saying more can be loaded is the difference between "there
  // are none" and "none so far".
  // Four cases, because with BOTH a search and a filter active the old
  // precedence named only the search — so an agent clears the search, the list
  // is still empty, and the chip was the cause all along. That is the same
  // misdirection this distinction was added to prevent, one case further in.
  const emptyMessage = getTranslate(
    filtering && clientFiltered
      ? 'No tickets match this search and filter.'
      : filtering
      ? 'No tickets match this search.'
      : clientFiltered
      ? hasMore
        ? 'No loaded tickets match this filter yet. Load more to keep looking.'
        : 'No loaded tickets match this filter.'
      : 'No tickets in this view.'
  )

  return (
    <div>
      {/* The heading names the CURRENT list, not the section. SupportTabs owns
          the section title and the tab strip. */}
      <SectionTitle title={getTranslate(TICKET_TAB_LABEL[tab])} />

      <TicketSearchInput value={query} onChange={setQuery} />

      {/* Quick filters over what is loaded. Chips rather than a dialog: three
          named states an agent actually asks for, visible without opening
          anything. */}
      {/* role="group" on the EXISTING row, not a new wrapper: filterRow is
          display:flex with gap, so an inner element would collapse the three
          chips into one flex item and drop the spacing between them. The count
          falls inside the group, which is right — it is the group's output. */}
      <div
        className={classes.filterRow}
        role="group"
        aria-label={getTranslate('Quick filters')}>
        {/* aria-pressed, because selection was carried by fill and colour
            alone — both presentational. A screen reader announced "Breaching,
            button" identically whether the filter was on or off. On role=button
            this is the toggle-button pattern, which also makes "press again to
            release" a convention the user already knows, so clearing stops
            being discoverable only by sight.

            Deliberately not radio/aria-checked: radios cannot be deselected,
            and clearing by re-clicking would contradict that announcement. */}
        {QUICK_FILTERS.map(({ key, label }) => (
          <Chip
            key={key}
            size="small"
            label={getTranslate(label)}
            color={quickFilter === key ? 'primary' : 'default'}
            variant={quickFilter === key ? 'default' : 'outlined'}
            clickable
            aria-pressed={quickFilter === key}
            onClick={() =>
              setQuickFilter((current) => (current === key ? 'none' : key))
            }
          />
        ))}
        {/* A live region, because this string is already the honest status
            readout — it changes on every filter toggle, search settle and Load
            more. Filtering removes and reorders rows while a screen-reader
            user's focus stays on the chip, with no other feedback.

            aria-atomic so the whole phrase is read, not just the digit that
            changed; polite because the debounced search fires this repeatedly. */}
        <Typography
          variant="caption"
          className={classes.filterCount}
          role="status"
          aria-live="polite"
          aria-atomic="true">
          {/* ONE key with placeholders, not concatenated words: Farsi orders
              these differently, and "{a} of {b} loaded" assembled from three
              translated fragments cannot be reordered by the translator.

              Says "loaded", never "total": the server has not told us how many
              exist, so a total would be a claim we cannot make. */}
          {getTranslate('{shown} of {loaded} loaded', {
            shown: visible.length,
            loaded: tickets.length
          })}
        </Typography>
      </div>

      {(error || validationError) && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch?.()}>
              {getTranslate('Retry')}
            </Button>
          }>
          {listLoadErrorMessage(
            error,
            validationError,
            'Could not load tickets.'
          )}
        </Alert>
      )}

      {/* Above the list, not below it: the bar acts on a selection made in the
          list, and a control that appears under a screenful of rows is not
          discoverable from where the selection was made. */}
      <BulkAssignBar
        selected={selectedTickets}
        onClear={clearSelection}
        onDone={() => refetch?.()}
        onBusyChange={setAssigning}
      />

      {/* Separate desktop and mobile components behind <Hidden> (§2). Duplicate
          LAYOUT, never behaviour — both render the same row data and the same
          navigation target. */}
      <SLAClockContext.Provider value={now}>
        {!isMobile ? (
          <SupportTicketsTable
            tickets={visible}
            loading={loading}
            settled={settled}
            emptyMessage={emptyMessage}
            reloadData={() => refetch?.()}
            selectedIds={selectedIds}
            onToggleSelected={toggleSelected}
            onToggleAll={toggleAll}
            allSelected={
              visible.length > 0 && selectedTickets.length === visible.length
            }
            someSelected={selectedTickets.length > 0}
            disabled={assigning}
          />
        ) : (
          <div className={classes.mobileList}>
            {visible.map((ticket: Ticket) => (
              <SupportTicketRowMobile
                key={ticket.id}
                ticket={ticket}
                onClick={openTicket}
                selected={selectedIds.has(ticket.id)}
                onToggleSelected={toggleSelected}
                selectionDisabled={assigning}
              />
            ))}
            {settled && !visible.length && (
              <Typography variant="body2" className={classes.mobileEmpty}>
                {emptyMessage}
              </Typography>
            )}
          </div>
        )}
      </SLAClockContext.Provider>

      {/* Cursor pagination: the token's presence is the only reliable signal
          that more exists — a full page is not, since the last page can be
          exactly page-sized. §2's numbered <Pagination> needs a page count the
          cursor protocol does not provide. */}
      {hasMore && (
        <div className={classes.loadMoreContainer}>
          <Button variant="outlined" disabled={loading} onClick={loadMore}>
            {getTranslate('Load more')}
          </Button>
        </div>
      )}
    </div>
  )
}
